import React, { useState } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
  Typography
} from "@mui/material";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useDealActions } from "store/actions-context";
import { BankStatementUploadResult } from "store/types";

type UploadType = "nav" | "transaction_nav" | "starting_nav_transactions_ending_nav";

const UPLOAD_TYPES = [
  { value: "nav" as const, label: "NAV Statement", fileLabels: ["NAV Statement"], fileKeys: ["last_nav"] },
  {
    value: "transaction_nav" as const,
    label: "Transaction + NAV Statement",
    fileLabels: ["Transactions", "NAV Statement"],
    fileKeys: ["transactions", "last_nav"]
  },
  {
    value: "starting_nav_transactions_ending_nav" as const,
    label: "Starting NAV + Transactions + Ending NAV Statement",
    fileLabels: ["Starting NAV", "Transactions", "Ending NAV Statement"],
    fileKeys: ["first_nav", "transactions", "last_nav"]
  }
];

const STEPS = ["Choose Type", "Select Files", "Uploading", "Summary"];

interface Props {
  open: boolean;
  onClose: () => void;
}

export const BankStatementUploadModal: React.FC<Props> = ({ open, onClose }) => {
  const { importBankStatementsAction } = useDealActions();
  const { action: importBankStatements } = useActionHook(importBankStatementsAction);

  const [activeStep, setActiveStep] = useState(0);
  const [uploadType, setUploadType] = useState<UploadType>("nav");
  const [files, setFiles] = useState<File[]>([]);
  const [result, setResult] = useState<BankStatementUploadResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleTypeChange = (value: UploadType) => {
    setUploadType(value);
    setFiles([]);
  };

  const handleUpload = async () => {
    setActiveStep(2);
    setUploadError(null);
    try {
      const result = await importBankStatements({ type: uploadType, files: files.filter(Boolean) as File[] });
      setResult(result);
      setActiveStep(3);
    } catch (e: any) {
      setUploadError(e.message ?? "Upload failed");
      setActiveStep(3);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setUploadType("nav");
    setFiles([]);
    setResult(null);
    setUploadError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={activeStep === 2 ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Bank Statements</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the type of data you want to upload.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {UPLOAD_TYPES.map((t) => {
                const selected = uploadType === t.value;
                return (
                  <Card
                    key={t.value}
                    variant="outlined"
                    sx={{ borderColor: selected ? "primary.main" : "divider", borderWidth: selected ? 2 : 1 }}
                  >
                    <CardActionArea onClick={() => handleTypeChange(t.value)}>
                      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Typography variant="body2" fontWeight={selected ? 600 : 400}>
                          {t.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.fileLabels.join(" · ")}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button variant="contained" onClick={() => setActiveStep(1)}>
                Next
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Button variant="outlined" component="label">
                Choose files
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".xlsx"
                  onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                />
              </Button>
              {files.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {files.length} file{files.length > 1 ? "s" : ""} selected
                </Typography>
              )}
            </Box>
            {files.map((f, i) => (
              <Chip key={i} label={f.name} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
              <Button onClick={() => setActiveStep(0)}>Back</Button>
              <Button variant="contained" onClick={handleUpload} disabled={files.length === 0}>
                Upload
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ py: 4 }}>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" align="center">
              Uploading and processing data...
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
              This may take a few minutes.
            </Typography>
          </Box>
        )}

        {activeStep === 3 && (
          <Box>
            {uploadError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {uploadError}
              </Alert>
            ) : (
              result && (
                <>
                  <Alert severity={result.statistics.failedRows > 0 ? "warning" : "success"} sx={{ mb: 2 }}>
                    Upload complete — {result.statistics.institutionName}
                  </Alert>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 2 }}>
                    {[
                      { label: "Total items", value: result.statistics.totalItems },
                      { label: "New deals", value: result.statistics.newDeals },
                      { label: "Created data rows", value: result.statistics.createdMeasureData },
                      { label: "Updated data rows", value: result.statistics.updatedMeasureData },
                      { label: "New measures", value: result.statistics.newMeasures },
                      { label: "Failed rows", value: result.statistics.failedRows },
                      { label: "NAV calculable deals", value: result.statistics.returnCalculableDeals },
                      { label: "IRR calculable deals", value: result.statistics.irrCalculableDeals }
                    ].map(({ label, value }) => (
                      <Box key={label}>
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {result.errors.length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Errors
                      </Typography>
                      <List dense disablePadding>
                        {result.errors.map((e, i) => (
                          <ListItem key={i} disablePadding sx={{ gap: 1, alignItems: "flex-start" }}>
                            <ErrorOutlineIcon color="error" sx={{ fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                            <ListItemText primary={<Typography variant="caption">{e}</Typography>} />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </>
              )
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button variant="contained" onClick={handleClose}>
                Done
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
